/**
 * Data Controls Types
 *
 * Core type definitions for client-side and server-side data filtering,
 * sorting, faceting, and search operations.
 */

// ============================================================================
// Filter Types
// ============================================================================

export type FilterOperator =
  | "eq" // Equal
  | "neq" // Not equal
  | "in" // In array
  | "nin" // Not in array
  | "gt" // Greater than
  | "gte" // Greater than or equal
  | "lt" // Less than
  | "lte" // Less than or equal
  | "contains" // String contains
  | "startsWith" // String starts with
  | "endsWith"; // String ends with

export interface FilterCondition {
  /** Field name (supports nested paths like 'user.profile.name') */
  field: string;
  /** Comparison operator */
  operator: FilterOperator;
  /** Value to compare against */
  value: string | number | boolean | (string | number)[];
}

export interface FilterGroup {
  /** Logical operator combining conditions */
  logic: "AND" | "OR";
  /** Conditions or nested filter groups */
  conditions: (FilterCondition | FilterGroup)[];
}

/** Root filter state - can be empty, a single group, or nested groups */
export type FilterState = FilterGroup | null;

// ============================================================================
// Sort Types
// ============================================================================

export interface SortState {
  /** Field to sort by (supports nested paths) */
  field: string;
  /** Sort direction */
  direction: "asc" | "desc";
}

// ============================================================================
// Search Types
// ============================================================================

export interface SearchConfig {
  /** Search query string */
  query: string;
  /** Fields to search across */
  fields: string[];
  /** Use fuzzy matching (Fuse.js) - default true */
  fuzzy?: boolean;
  /** Fuse.js matching threshold (0.0 = perfect, 1.0 = anything) - default 0.3 */
  threshold?: number;
}

// ============================================================================
// Facet Types
// ============================================================================

export type FacetType = "string" | "number" | "boolean" | "date";

export interface FacetConfig {
  /** Field name to facet on */
  field: string;
  /** Display label for the facet */
  label: string;
  /** Data type of the field */
  type?: FacetType;
  /** Optional: Provide predefined values for the facet (useful for enums) */
  values?: Array<{ value: string | number; label: string }>;

  // Phase 1: Progressive disclosure and customization
  /** Number of options to show before requiring search (default: 10) */
  searchThreshold?: number;
  /** Max options to show initially, with "Show all" button for rest (default: 50) */
  maxVisibleOptions?: number;
  /** Optional description text to show below the label */
  description?: string;
  /** Optional: Custom render function for option labels (e.g., to show Badges) */
  renderLabel?: (value: string, label: string) => React.ReactNode;
  /** Optional: Group name for organizing filters in FilterDialog */
  group?: string;
}

export interface FacetCounts {
  [field: string]: {
    [value: string]: number;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a single filter condition
 */
export function createFilter(
  field: string,
  operator: FilterOperator,
  value: string | number | boolean | (string | number)[]
): FilterCondition {
  return { field, operator, value };
}

/**
 * Create a filter group from multiple conditions with AND logic
 */
export function createFilters(conditions: FilterCondition[], logic: "AND" | "OR" = "AND"): FilterGroup {
  return { logic, conditions };
}

/**
 * Create an empty filter state
 */
export function createEmptyFilter(): FilterGroup {
  return { logic: "AND", conditions: [] };
}

/**
 * Check if a filter state is empty (no conditions)
 */
export function isFilterEmpty(filter: FilterState): boolean {
  if (!filter) return true;
  if (filter.conditions.length === 0) return true;

  // Check nested groups
  return filter.conditions.every((condition) => {
    if ("conditions" in condition) {
      return isFilterEmpty(condition);
    }
    return false;
  });
}

/**
 * Convert structured filter state to simple display chips
 * Used for backward compatibility with existing FilterChips component
 */
export interface FilterChip {
  id: string;
  label: string;
  value: string;
}

export function filterToChips(filter: FilterState, facetConfigs?: FacetConfig[]): FilterChip[] {
  if (!filter) return [];

  const configMap = new Map(facetConfigs?.map((c) => [c.field, c]));

  // Group conditions by field to consolidate multiple conditions for the same field
  const conditionsByField = new Map<string, FilterCondition[]>();

  function collectConditions(condition: FilterCondition | FilterGroup) {
    if ("conditions" in condition) {
      // It's a group, recurse
      condition.conditions.forEach((c) => collectConditions(c));
    } else {
      // It's a condition - add to field group
      const existing = conditionsByField.get(condition.field) || [];
      existing.push(condition);
      conditionsByField.set(condition.field, existing);
    }
  }

  filter.conditions.forEach((condition) => collectConditions(condition));

  // Convert grouped conditions to chips
  const chips: FilterChip[] = [];

  for (const [field, conditions] of conditionsByField.entries()) {
    const config = configMap.get(field);
    const label = config?.label || field;

    // Collect all values for "in" and "eq" operators to combine them
    const inValues: (string | number)[] = [];
    const rangeConditions: { operator: string; value: string | number }[] = [];
    const otherConditions: FilterCondition[] = [];

    for (const condition of conditions) {
      if (condition.operator === "in" && Array.isArray(condition.value)) {
        inValues.push(...condition.value);
      } else if (condition.operator === "eq") {
        // Only add non-boolean, non-array values to inValues
        if (typeof condition.value !== "boolean" && !Array.isArray(condition.value)) {
          inValues.push(condition.value);
        } else {
          otherConditions.push(condition);
        }
      } else if (["gte", "gt", "lte", "lt"].includes(condition.operator)) {
        rangeConditions.push({
          operator: condition.operator,
          value:
            typeof condition.value === "boolean" || Array.isArray(condition.value)
              ? String(condition.value)
              : condition.value,
        });
      } else {
        otherConditions.push(condition);
      }
    }

    // Create chip for "in"/"eq" values if any
    if (inValues.length > 0) {
      // Deduplicate values before displaying
      const uniqueValues = Array.from(new Set(inValues));

      // Map values to their display labels if available in config
      const displayValues = uniqueValues.map((val) => {
        const valueOption = config?.values?.find((v) => String(v.value) === String(val));
        return valueOption?.label ?? val;
      });

      chips.push({
        id: `${field}-in`,
        label,
        value: displayValues.join(", "),
      });
    }

    // Create chip for range conditions if any (combine gte/lte into a range display)
    if (rangeConditions.length > 0) {
      const gteCondition = rangeConditions.find((c) => c.operator === "gte" || c.operator === "gt");
      const lteCondition = rangeConditions.find((c) => c.operator === "lte" || c.operator === "lt");

      if (gteCondition && lteCondition) {
        // Both min and max - show as range
        const minSymbol = gteCondition.operator === "gte" ? "≥" : ">";
        const maxSymbol = lteCondition.operator === "lte" ? "≤" : "<";
        chips.push({
          id: `${field}-range`,
          label,
          value: `${minSymbol} ${gteCondition.value} and ${maxSymbol} ${lteCondition.value}`,
        });
      } else {
        // Only one side of range
        for (const rc of rangeConditions) {
          let symbol: string;
          if (rc.operator === "gte") symbol = "≥";
          else if (rc.operator === "gt") symbol = ">";
          else if (rc.operator === "lte") symbol = "≤";
          else symbol = "<";

          chips.push({
            id: `${field}-${rc.operator}`,
            label,
            value: `${symbol} ${rc.value}`,
          });
        }
      }
    }

    // Create chips for other operators
    for (const condition of otherConditions) {
      let displayValue: string;
      if (condition.operator === "neq") {
        displayValue = `≠ ${condition.value}`;
      } else if (condition.operator === "contains") {
        displayValue = `contains "${condition.value}"`;
      } else if (condition.operator === "startsWith") {
        displayValue = `starts with "${condition.value}"`;
      } else if (condition.operator === "endsWith") {
        displayValue = `ends with "${condition.value}"`;
      } else {
        displayValue = String(condition.value);
      }

      chips.push({
        id: `${field}-${condition.operator}`,
        label,
        value: displayValue,
      });
    }
  }

  return chips;
}

/**
 * Add a condition to a filter state
 */
export function addFilterCondition(filter: FilterState, condition: FilterCondition): FilterGroup {
  const existing = filter || createEmptyFilter();
  return {
    ...existing,
    conditions: [...existing.conditions, condition],
  };
}

/**
 * Remove a condition from a filter state by field
 */
export function removeFilterCondition(filter: FilterState, field: string): FilterGroup {
  if (!filter) return createEmptyFilter();

  const conditions = filter.conditions.filter((condition) => {
    if ("conditions" in condition) {
      // Recursively remove from nested groups
      const cleaned = removeFilterCondition(condition, field);
      return cleaned.conditions.length > 0;
    }
    return condition.field !== field;
  });

  return {
    ...filter,
    conditions,
  };
}

/**
 * Get all unique fields being filtered on
 */
export function getFilterFields(filter: FilterState): string[] {
  if (!filter) return [];

  const fields = new Set<string>();

  function traverse(condition: FilterCondition | FilterGroup) {
    if ("conditions" in condition) {
      condition.conditions.forEach(traverse);
    } else {
      fields.add(condition.field);
    }
  }

  filter.conditions.forEach(traverse);
  return Array.from(fields);
}
